/**
 * V2 Site Group Settings page — companion to `SiteSettingsViewV2`.
 *
 * Reads/writes the consolidated `/api/odl-app/site-group-settings/{id}/`
 * endpoint via the `features/siteGroupSettings/` hooks.  Mirrors the
 * site-settings V2 surface in shape, gating idiom (section presence
 * is the visibility signal — no sidecar capability block), and save
 * flow. Each section's own fields/handlers live under
 * `sections/sitegroup/`; this file is the shell — routing, formik-
 * value normalization, and save orchestration.
 *
 * Skeleton commit ships the General section plus the two
 * out-of-form lifecycle actions (customer reassignment + deactivate
 * / reactivate) — these stay outside the unified Save bar in legacy
 * and we preserve that UX 1:1.  Subsequent commits layer on
 * Curfew, Admin (Options + Features + Instructions), Sharing,
 * Contacts, and Smart Weather to match the legacy tab surface.
 *
 * The legacy V1 (`SiteGroupSettingsView.jsx`) stays the active
 * mount until V2 reaches feature parity; flip the import in
 * `routes.js` + `views/settings/index.jsx` once that lands.
 */

import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { selectCustomer } from '@sophocles/features/auth/authSelectors';
import { loadSiteGroupWeatherConfigurationForSiteGroup } from '@sophocles/features/siteGroups/api';
import { useSiteGroupSettings } from '@sophocles/features/siteGroupSettings';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import { createAutoShares, removeAutoShares } from '@sophocles/features/virtual/api';
import { createAddress, editAddress } from '@sophocles/features/warehouse/orders/api';
import useBreadcrumbs from '@sophocles/hooks/useBreadcrumbs';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteGroup from '@sophocles/hooks/useSiteGroup';
import { SettingsForm, SettingsPageShell } from '@sophocles/views/settings/components';

import AdminSection from './sections/sitegroup/AdminSection';
import ContactsSection from './sections/sitegroup/ContactsSection';
import CurfewSection from './sections/sitegroup/CurfewSection';
import GeneralSection from './sections/sitegroup/GeneralSection';
import SharingSection from './sections/sitegroup/SharingSection';
import SiteGroupDangerZone from './sections/sitegroup/SiteGroupDangerZone';
import SmartWeatherSection from './sections/sitegroup/SmartWeatherSection';

// Yup schema mirrors SiteGroup model constraints.  `name` max-length
// is 30 (model cap); the legacy UI tightens to 15 for non-virtual
// groups but that's a UX rule, not a model rule — leave at 30 here
// and surface the stricter UX cap as a separate UX rule when we
// fold in the virtual-vs-physical distinction.  `description` is
// optional, max 100 on the model.
const validationSchema = yup.object({
  general: yup
    .object({
      name: yup.string().trim().required('Name is required.').max(30, 'Name must be 30 characters or fewer.'),
      description: yup.string().trim().nullable().max(100, 'Description must be 100 characters or fewer.'),
    })
    .nullable(),
  curfew: yup
    .object({
      enabled: yup.boolean().nullable(),
      start_time: yup.string().nullable(),
      end_time: yup.string().nullable(),
      trim_schedules: yup.boolean().nullable(),
    })
    .nullable(),
  admin: yup
    .object({
      options: yup.object().nullable(),
    })
    .nullable(),
});

function SiteGroupSettingsViewV2() {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const siteGroupId = Number(params.sitegroup_id);
  const customerId = Number(params.cust_id);

  useCustomer(customerId);
  const { loading: plantLoading } = useSiteGroup(siteGroupId);

  const customer = useSelector(selectCustomer(customerId));
  const isBillboard = customer?.is_billboard ?? false;
  const { settings, status, save } = useSiteGroupSettings({ siteGroupId });
  const { permissions } = useUser();
  const autoBreadcrumbs = useBreadcrumbs();
  const canEdit = permissions.canEditSiteGroup;

  const separateTenant = useSelector(selectSeparateTenant);

  const hasAdmin = Boolean(settings?.admin) && !separateTenant;
  const hasContacts = Boolean(settings?.contacts) && !(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__);
  // An inventory group holds stock and never holds sites, so the sections
  // that only describe a group's sites have nothing to act on: sharing hands
  // sites to another customer, and curfew and smart weather schedule the
  // loads on them. The flag rides the `inventory` section, which the backend
  // serves to whoever may know a group is a warehouse at all.
  const isInventoryGroup = Boolean(settings?.inventory?.is_inventory);
  const hasSharing = Boolean(settings?.sharing) && !isInventoryGroup;
  const hasCurfew = Boolean(settings?.curfew) && !separateTenant && !isInventoryGroup;
  const hasSmartWeather = Boolean(settings?.smart_weather) && !separateTenant && !isInventoryGroup;

  const weatherRef = React.useRef(null);
  React.useEffect(() => {
    if (siteGroupId && hasSmartWeather) {
      dispatch(loadSiteGroupWeatherConfigurationForSiteGroup(siteGroupId));
    }
  }, [dispatch, siteGroupId, hasSmartWeather]);

  const isFullyViewOnly = !canEdit && !(hasCurfew && permissions.canEditSiteGroupCurfew);

  // Sidebar sections — driven by section presence on the response.
  // Memoized so SettingsPageShell's registration effect doesn't
  // re-fire on every render.
  const sections = React.useMemo(() => {
    const list = [{ id: 'settings-general', title: t('siteGroupSettings.v2.general', 'General'), icon: 'info' }];
    if (hasAdmin) {
      list.push({
        id: 'settings-admin',
        title: t('siteGroupSettings.v2.admin', 'Admin'),
        icon: 'adminPanelSettings',
        pillText: t('siteSettings.adminOnly', 'Admin Only'),
      });
    }
    if (hasContacts) {
      list.push({
        id: 'settings-contacts',
        title: t('siteGroupSettings.v2.contacts', 'Contacts'),
        icon: 'contactPhone',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (hasSharing) {
      list.push({
        id: 'settings-sharing',
        title: t('siteGroupSettings.v2.sharing', 'Sharing'),
        icon: 'verifiedUser',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (hasCurfew) {
      list.push({
        id: 'settings-curfew',
        title: t('siteGroupSettings.v2.curfew', 'Curfew'),
        icon: 'nightsStay',
      });
    }
    if (hasSmartWeather) {
      list.push({
        id: 'settings-smart-weather',
        title: t('siteGroupSettings.v2.smartWeather', 'Smart Weather'),
        icon: 'thunderstorm',
      });
    }
    return list;
  }, [t, hasAdmin, hasContacts, hasSharing, hasCurfew, hasSmartWeather]);

  if (!settings || status === 'loading' || plantLoading) {
    return <LoadingScreen />;
  }

  const initialValues = {
    general: settings.general || {},
    ...(hasAdmin
      ? {
          admin: {
            // Coerce the read-side nested user objects back into ids
            // for the *_id write fields (the LimitedSiteGroupOptions
            // serializer round-trips like this — read-only nested
            // user reps + write_only `_id` fields).
            options: {
              ...(!__EXCLUDE_BILLING__
                ? {
                    billable: Boolean(settings.admin?.options?.billable),
                    require_sites_bill_alternate: Boolean(settings.admin?.options?.require_sites_bill_alternate),
                  }
                : {}),
              ops_manager_id: settings.admin?.options?.ops_manager?.id ?? null,
              general_manager_id: settings.admin?.options?.general_manager?.id ?? null,
              regional_manager_id: settings.admin?.options?.regional_manager?.id ?? null,
              territory_manager_id: settings.admin?.options?.territory_manager?.id ?? null,
              area_field_manager_id: settings.admin?.options?.area_field_manager?.id ?? null,
            },
          },
        }
      : {}),
    ...(hasCurfew
      ? {
          curfew: {
            enabled: Boolean(settings.curfew?.enabled),
            start_time: settings.curfew?.start_time || '22:00:00',
            end_time: settings.curfew?.end_time || '05:00:00',
            // Write-only flag — the server pops it before saving and
            // uses it to decide whether to fire the schedule-trim
            // task.  Reset to false on each load so the dirty diff
            // doesn't carry it forward across saves.
            trim_schedules: false,
          },
        }
      : {}),
    ...(hasSharing
      ? {
          // Synthetic Formik field — pending share additions /
          // removals are pushed in here by ThirdPartiesTab's
          // onChange so dirty-tracking activates the global Save
          // bar.  Stripped out before the settings PATCH;
          // dispatched as separate AutoShare mutations in
          // handleSave.
          __pending_shares: { newlyShared: [], newlyUnshared: [] },
        }
      : {}),
    // Synthetic Formik field — BillingShippingAddresses' onChange
    // pushes the latest billing/shipping snapshots in here on every
    // user edit so the Save bar activates.  Initial value is null
    // so an unmodified visit doesn't dirty the form.  Dispatched
    // as separate Address mutations in handleSave.  Omitted when
    // addresses are excluded at build time.
    ...(!(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__) ? { __pending_addresses: null } : {}),
    // Synthetic Formik field — flips to true whenever the
    // embedded SiteGroupWeatherConfigurationTab Formik form goes
    // dirty (signaled via onDirtyChange).  Stripped before the
    // section PATCH; on save we call weatherRef.current.submit()
    // which dispatches patchSiteGroupWeatherConfiguration.
    ...(hasSmartWeather ? { __pending_weather_dirty: false } : {}),
  };

  // Save flow splits across the consolidated section PATCH plus
  // synthetic-field-driven side effects:
  //
  //   1. Section PATCH for every section in the diff (excluding
  //      synthetic __pending_* fields).
  //   2. AutoShare create / remove for ThirdPartiesTab edits
  //      (__pending_shares).
  //   3. Address create / patch for billing + shipping
  //      (__pending_addresses).
  //
  // Each piece runs sequentially so a failure on any surfaces
  // through SettingsForm's try/catch.  Mirrors the site-settings
  // V2 pattern.
  const handleSave = async diff => {
    const { __pending_shares, __pending_addresses, __pending_weather_dirty, ...settingsDiff } = diff;
    if (Object.keys(settingsDiff).length > 0) {
      await save(settingsDiff);
    }
    if (__pending_shares?.newlyUnshared?.length > 0) {
      await dispatch(removeAutoShares(__pending_shares.newlyUnshared.map(u => u.id))).unwrap();
    }
    if (__pending_shares?.newlyShared?.length > 0) {
      await dispatch(createAutoShares(__pending_shares.newlyShared)).unwrap();
    }
    if (__pending_addresses) {
      // editOrCreateAddress: existing rows have an id and route to
      // patch; new rows route to post.  Inlined to keep the legacy
      // shim out of V2.
      const upsertAddress = addr => {
        if (!addr) return Promise.resolve();
        return addr.id == null ? dispatch(createAddress(addr)).unwrap() : dispatch(editAddress(addr)).unwrap();
      };
      await Promise.all([upsertAddress(__pending_addresses.billing), upsertAddress(__pending_addresses.shipping)]);
    }
    // Smart Weather form lives in its own embedded Formik; drive
    // its submit through the imperative ref handle so the unified
    // Save bar owns the lifecycle.  The internal handleSubmit
    // dispatches patchSiteGroupWeatherConfiguration and returns
    // its Promise — await it so any backend errors surface here.
    if (__pending_weather_dirty && weatherRef.current?.submit) {
      await weatherRef.current.submit();
    }
  };

  const settingsLabel = t('siteGroupSettings.v2.title', 'Settings');
  const breadcrumbs = (() => {
    const autoCrumbs = autoBreadcrumbs.slice();
    const hasGroupCrumb =
      autoCrumbs.length > 0 && autoCrumbs[autoCrumbs.length - 1].to?.includes(`/plant/${siteGroupId}`);
    if (!hasGroupCrumb && customer?.id && settings.general?.name) {
      autoCrumbs.push({
        label: settings.general.name,
        to: `/c/${customer.id}/plant/${siteGroupId}/structures`,
      });
    }
    return [...autoCrumbs, { label: settingsLabel }];
  })();

  const siteGroupForActions = {
    id: siteGroupId,
    customer_id: customer?.id,
    is_virtual: settings.general?.is_virtual ?? false,
  };
  const deactivated = settings.general?.deactivated ?? false;

  return (
    <SettingsPageShell
      page="site_group_settings"
      breadcrumbs={breadcrumbs}
      contextName="site_group"
      basePath={`/c/${customerId}/plant/${siteGroupId}`}
      sections={sections}
      sectionParam={params.section}
      viewOnly={isFullyViewOnly}
      viewOnlyMessage={t(
        'siteGroupSettings.v2.viewOnly',
        "You can view this site group's settings but don't have permission to edit. Contact an administrator to request edit access.",
      )}
    >
      <SettingsForm
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSave={handleSave}
        readOnly={!canEdit}
      >
        {/*
              Per-section disable wrappers — different sections take
              different perms (general → change_sitegroup, curfew →
              change_sitegroupcurfew), and a single umbrella
              `<fieldset disabled>` would block legitimate edits from
              users who hold one perm but not another.  HTML
              `<fieldset disabled>` propagates to every nested form
              control, so we just wrap each SettingsSection in its
              own fieldset gated on its own perm.
            */}
        <Stack spacing={3}>
          <Stack component="fieldset" disabled={!canEdit} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            <GeneralSection
              siteGroupId={siteGroupId}
              siteGroup={siteGroupForActions}
              customer={customer}
              settings={settings}
            />
          </Stack>

          <SiteGroupDangerZone
            siteGroup={siteGroupForActions}
            customer={customer}
            isBillboard={isBillboard}
            deactivated={deactivated}
          />

          {hasAdmin ? (
            <Stack component="fieldset" disabled={!canEdit} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
              <AdminSection siteGroupId={siteGroupId} customerId={customerId} isBillboard={isBillboard} />
            </Stack>
          ) : null}

          {hasContacts ? <ContactsSection customerId={Number(customerId)} siteGroupId={siteGroupId} /> : null}

          {hasSharing ? (
            <SharingSection
              customerId={Number(customerId)}
              siteGroup={{ id: siteGroupId, description: settings.general?.description }}
            />
          ) : null}

          {hasCurfew ? (
            <Stack
              component="fieldset"
              disabled={!permissions.canEditSiteGroupCurfew}
              sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
            >
              <CurfewSection disabled={!permissions.canEditSiteGroupCurfew} isBillboard={isBillboard} />
            </Stack>
          ) : null}

          {hasSmartWeather ? <SmartWeatherSection ref={weatherRef} siteGroupId={siteGroupId} /> : null}
        </Stack>
      </SettingsForm>
    </SettingsPageShell>
  );
}

export default SiteGroupSettingsViewV2;
